/**
 * ENVIRONMENT: PRODUCTION
 * MODULE: Central Consumer Directory & Compliance Hub (B2C)
 * PATH: src/routes/users/UserList.js
 * COMPLIANCE: Digital Personal Data Protection (DPDP) Act 2023
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Table,
  Button,
  Switch,
  Space,
  Row,
  Col,
  Card,
  Tag,
  Tooltip,
  Typography,
  Input,
  message,
} from "antd";
import {
  SearchOutlined,
  ReloadOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  SafetyCertificateOutlined,
  CheckCircleFilled,
  ClockCircleFilled,
  EditOutlined,
  UserOutlined,
} from "@ant-design/icons";
import _ from "lodash";
import { parseQueryParams, stringifyQueryParams } from "utils/url";
import UserUpdateForm from "./UserUpdateForm";
import api from "utils/api";
import { getUsers, updateUser } from "requests/user";
import moment from "moment";
import "./UserList.css";

const { Text, Title } = Typography;

const UserList = () => {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(
    Number(process.env.REACT_APP_RECORDS_PER_PAGE) || 10
  );
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [records, setRecords] = useState([]);
  const [visibleUpdateForm, setVisibleUpdateForm] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();

  // Initialize search input from URL parameters
  const initialParams = parseQueryParams(location);
  const [searchTerm, setSearchTerm] = useState(
    initialParams.search || initialParams.keyword || ""
  );

  // DPDP Email Unmasking Map
  const [revealedEmails, setRevealedEmails] = useState({});

  // Central record fetcher
  const getRecords = useCallback(
    async (query) => {
      try {
        setIsTableLoading(true);
        const activeQuery = query || parseQueryParams(location);
        
        // Pass both 'search' and 'keyword' to ensure compatibility with backend controller
        const currentSearch = activeQuery.search || activeQuery.keyword || "";
        const updatedQuery = {
          ...activeQuery,
          status: "all",
          search: currentSearch,
          keyword: currentSearch,
        };

        const response = await getUsers(updatedQuery);

        if (response && response.data) {
          setRecords(response.data);
          setPage(Number(response.current_page) || 1);
          setPerPage(Number(response.per_page) || 10);
          setTotalCount(Number(response.total) || 0);
        } else {
          setRecords([]);
          setTotalCount(0);
        }
      } catch (err) {
        message.error(err?.response?.data?.message || "Failed to load consumer directory.");
      } finally {
        setIsTableLoading(false);
      }
    },
    [location]
  );

  useEffect(() => {
    const query = parseQueryParams(location);
    getRecords(query);
  }, [location, getRecords]);

  // Execute Search by pushing to URL query state
  const executeSearch = useCallback(
    (value) => {
      const trimmed = (value || "").trim();
      let query = parseQueryParams(location);

      query = {
        ...query,
        page: 1,
      };

      if (trimmed) {
        query.search = trimmed;
        query.keyword = trimmed;
      } else {
        delete query.search;
        delete query.keyword;
      }

      navigate({
        pathname: location.pathname,
        search: stringifyQueryParams(query),
      });
    },
    [location, navigate]
  );

  // Debounced search for smooth typing without lag
  const debouncedSearch = useRef(
    _.debounce((val) => {
      executeSearch(val);
    }, 450)
  ).current;

  const handleInputChange = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    debouncedSearch(val);
  };

  const handleRefresh = () => {
    setSearchTerm("");
    let query = parseQueryParams(location);
    delete query.search;
    delete query.keyword;
    query.page = 1;
    navigate({
      pathname: location.pathname,
      search: stringifyQueryParams(query),
    });
  };

  // DPDP Masking Helper
  const maskEmail = (email) => {
    if (!email || !email.includes("@")) return "N/A";
    const [user, domain] = email.split("@");
    if (user.length <= 2) return `${user}•••@${domain}`;
    return `${user.slice(0, 2)}${"•".repeat(Math.min(user.length - 2, 6))}@${domain}`;
  };

  // Status Toggle
  const handleUpdateStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === 1 || currentStatus === "1" ? 0 : 1;
    try {
      setIsTableLoading(true);
      const response = await api.post("/admin/user-change-status", {
        id,
        status: newStatus,
      });

      if (response.data?.status === "1" || response.data?.status === 1) {
        message.success(response.data.message || "Customer status updated.");
        getRecords();
      } else {
        message.error(response.data?.message || "Failed to update account status.");
      }
    } catch (error) {
      message.error("Error communicating with authentication switch.");
    } finally {
      setIsTableLoading(false);
    }
  };

  // KYC Verification State Switch Toggle
  const handleUpdateKycStatus = async (id, checked) => {
    try {
      const response = await api.post("/admin/update-verification-status", {
        id,
        verify_status: checked ? 1 : 0,
      });

      if (response.data?.status === "1" || response.data?.status === 1) {
        message.success(response.data.message || "KYC verification status updated.");
        setRecords((prev) =>
          prev.map((item) =>
            (item._id || item.id) === id
              ? { ...item, verify_status: checked ? 1 : 0 }
              : item
          )
        );
      } else {
        message.error(response.data?.message || "Unable to update KYC state.");
      }
    } catch (error) {
      message.error("Network exception while updating KYC verification status.");
    }
  };

  const onChangeTable = (pagination) => {
    let query = parseQueryParams(location);
    query = {
      ...query,
      page: pagination.current,
      per_page: pagination.pageSize,
    };

    navigate({
      pathname: location.pathname,
      search: stringifyQueryParams(query),
    });
  };

  const onEdit = (data) => {
    setSelectedUser(data);
    setVisibleUpdateForm(true);
  };

  const onView = (id) => {
    navigate(`/user-detail/${id}`);
  };

  const onUpdateUser = async (data) => {
    try {
      await updateUser(selectedUser._id || selectedUser.id, data);
      message.success("Customer profile updated.");
      setVisibleUpdateForm(false);
      setSelectedUser(null);
      getRecords();
    } catch (err) {
      message.error("Failed to update profile.");
    }
  };

  const columns = [
    {
      title: "SR",
      width: 60,
      align: "center",
      render: (_, __, index) => (
        <span className="user-sr">{(page - 1) * perPage + index + 1}</span>
      ),
    },
    {
      title: "Customer Profile",
      width: 220,
      render: (_, record) => (
        <div className="consumer-profile-cell">
          <div className="consumer-avatar">
            <UserOutlined />
          </div>
          <div className="consumer-meta">
            <span className="consumer-name">{record.fullname || "Unnamed Customer"}</span>
            <Tag color="cyan" className="consumer-type-tag">
              Direct B2C Consumer
            </Tag>
          </div>
        </div>
      ),
    },
    {
      title: "Contact Details (DPDP Masked)",
      width: 240,
      render: (_, record) => {
        const isRevealed = revealedEmails[record._id || record.id];
        return (
          <div className="consumer-contact-cell">
            <div className="email-display-row">
              <span className="masked-email">
                {isRevealed ? record.email : maskEmail(record.email)}
              </span>
              {record.email && (
                <Button
                  type="text"
                  size="small"
                  className="unmask-action-btn"
                  icon={isRevealed ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                  onClick={() =>
                    setRevealedEmails((prev) => ({
                      ...prev,
                      [record._id || record.id]: !prev[record._id || record.id],
                    }))
                  }
                />
              )}
            </div>
            {record.mobile && (
              <span className="contact-phone">+91 {record.mobile}</span>
            )}
          </div>
        );
      },
    },
    {
      title: "Registration Date",
      width: 170,
      render: (_, record) => (
        <div className="date-cell">
          <span className="reg-date">
            {record.created_at
              ? moment(record.created_at).format("DD MMM YYYY")
              : "N/A"}
          </span>
          <span className="reg-time">
            {record.created_at
              ? moment(record.created_at).format("hh:mm A")
              : ""}
          </span>
        </div>
      ),
    },
    {
      title: "Account Status",
      width: 130,
      align: "center",
      render: (_, record) => {
        const isActive = record.status === 1 || record.status === "1";
        return (
          <div className="account-switch-wrap">
            <Switch
              checked={isActive}
              checkedChildren="Active"
              unCheckedChildren="Inactive"
              onChange={() => handleUpdateStatus(record._id || record.id, record.status)}
              className={isActive ? "switch-active" : "switch-inactive"}
            />
          </div>
        );
      },
    },
    {
      title: "KYC Verification",
      width: 160,
      align: "center",
      render: (_, record) => {
        const isKycVerified = Number(record.verify_status) === 1;
        return (
          <div className="kyc-control-wrap">
            <Tag
              icon={isKycVerified ? <CheckCircleFilled /> : <ClockCircleFilled />}
              color={isKycVerified ? "success" : "warning"}
              className="kyc-badge"
            >
              {isKycVerified ? "Verified" : "Pending KYC"}
            </Tag>
            <div style={{ marginTop: 4 }}>
              <Switch
                size="small"
                checked={isKycVerified}
                onChange={(checked) =>
                  handleUpdateKycStatus(record._id || record.id, checked)
                }
              />
            </div>
          </div>
        );
      },
    },
    {
      title: "Actions",
      width: 120,
      align: "right",
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="Edit Customer Details">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => onEdit(record)}
              className="action-icon-btn"
            />
          </Tooltip>

          <Tooltip title="View Account Profile">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => onView(record._id || record.id)}
              className="action-icon-btn"
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div className="user-directory-container">
      {/* Enterprise Header Bar */}
      <Card className="user-header-card" bordered={false}>
        <Row justify="space-between" align="middle" gutter={[16, 16]}>
          <Col xs={24} lg={10}>
            <div className="user-title-wrap">
              <SafetyCertificateOutlined className="header-shield-icon" />
              <div>
                <Title level={4} className="user-main-heading">
                  Customer Directory &amp; Compliance Hub
                </Title>
                <Text type="secondary" className="user-subtext">
                  Direct B2C accounts, identity verification &amp; privacy controls
                </Text>
              </div>
            </div>
          </Col>

          <Col xs={24} lg={14} className="user-controls-wrap">
            <Space size="middle" className="controls-flex">
              <Input
                placeholder="Search Customer Name, Email, or Mobile..."
                prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
                value={searchTerm}
                onChange={handleInputChange}
                onPressEnter={(e) => executeSearch(e.target.value)}
                allowClear
                className="user-search-input"
              />

              <Button
                icon={<ReloadOutlined />}
                onClick={handleRefresh}
                className="user-refresh-btn"
              />
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Main Customers Table */}
      <Card className="user-table-card" bordered={false}>
        <Table
          loading={isTableLoading}
          dataSource={records}
          columns={columns}
          onChange={onChangeTable}
          rowKey={(record) => record.id || record._id}
          pagination={{
            pageSize: perPage,
            total: totalCount,
            current: page,
            showSizeChanger: true,
            pageSizeOptions: ["10", "25", "50", "100"],
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} verified consumers`,
          }}
          scroll={{ x: 1000 }}
          className="enterprise-user-table"
        />
      </Card>

      {/* Update User Form Modal */}
      {selectedUser && (
        <UserUpdateForm
          record={{
            ...selectedUser,
            role: selectedUser.role?.id || selectedUser.role || "user",
          }}
          visible={visibleUpdateForm}
          onClose={() => {
            setVisibleUpdateForm(false);
            setSelectedUser(null);
          }}
          onSubmit={onUpdateUser}
        />
      )}
    </div>
  );
};

export default UserList;